import { z } from "zod";

export const QuotaResponseSchema = z.object({
  freeRemaining: z.number().int().min(0).max(10),
  paidRemaining: z.number().int().min(0),
  nextPaidExpiry: z.string().datetime().nullable(),
}).strict();

export const RedeemResponseSchema = z.object({
  restored: z.boolean(),
  quota: QuotaResponseSchema,
}).strict();

export const AdminPackageSchema = z.object({
  id: z.string().uuid(),
  name: z.string().trim().min(1).max(80),
  credits: z.number().int().min(1).max(100_000),
  testPriceMinor: z.number().int().min(0),
  currency: z.string().regex(/^[A-Z]{3}$/),
  validityDays: z.number().int().min(1).max(3_650),
  enabled: z.boolean(),
}).strict();

export const AdminPackageInputSchema = z.object({
  id: z.string().uuid().optional(),
  name: z.string().trim().min(1).max(80),
  credits: z.number().int().min(1).max(100_000),
  testPriceMinor: z.number().int().min(0),
  currency: z.string().regex(/^[A-Z]{3}$/),
  validityDays: z.number().int().min(1).max(3_650),
  enabled: z.boolean(),
}).strict();

export const AdminSummarySchema = z.object({
  localDay: z.string().date(),
  freeSuccessful: z.number().int().min(0),
  paidSuccessful: z.number().int().min(0),
}).strict();

export const AdminRedemptionBatchSchema = z.object({
  codes: z.array(z.string().min(1)).min(1).max(100),
  displayOnce: z.literal(true),
}).strict();

export const AdminPackagesResponseSchema = z.object({
  packages: z.array(AdminPackageSchema),
}).strict();

export const AdminLoginResponseSchema = z.object({
  authenticated: z.literal(true),
}).strict();

export const AdminRedemptionRequestSchema = z.object({
  packageId: z.string().uuid(),
  count: z.number().int().min(1).max(100),
}).strict();

export const AdminDisableRedemptionResponseSchema = z.object({
  publicId: z.string().min(1),
  disabled: z.literal(true),
}).strict();

export const AdminReissueRedemptionResponseSchema = z.object({
  publicId: z.string().min(1),
  disabled: z.literal(true),
  reissuedCode: z.string().min(1),
  displayOnce: z.literal(true),
}).strict();

export const AiProviderSchema = z.object({
  id: z.string().uuid(),
  name: z.string().trim().min(1).max(80),
  baseUrl: z.string().trim().min(1).max(512),
  apiKeyMasked: z.string(),
  selectedModel: z.string().trim().min(1).max(200),
  inputPriceMinor: z.number().int().min(0),
  outputPriceMinor: z.number().int().min(0),
  enabled: z.boolean(),
  isActive: z.boolean(),
  models: z.array(z.string().trim().min(1).max(200)).max(200),
}).strict();

export const AiProviderInputSchema = z.object({
  id: z.string().uuid().optional(),
  name: z.string().trim().min(1).max(80),
  baseUrl: z.string().trim().min(1).max(512),
  apiKey: z.string().max(1024).optional(),
  selectedModel: z.string().trim().min(1).max(200),
  inputPriceMinor: z.number().int().min(0).optional(),
  outputPriceMinor: z.number().int().min(0).optional(),
  enabled: z.boolean().optional(),
}).strict();

export const AiProvidersResponseSchema = z.object({
  providers: z.array(AiProviderSchema),
  activeProviderId: z.string().uuid().nullable(),
}).strict();

export const AiUsageRowSchema = z.object({
  provider: z.string().min(1),
  model: z.string().min(1),
  calls: z.number().int().min(0),
  promptTokens: z.number().int().min(0),
  completionTokens: z.number().int().min(0),
  costMinor: z.number().int().min(0),
}).strict();

export const AiUsageSummarySchema = z.object({
  localDay: z.string().date(),
  calls: z.number().int().min(0),
  promptTokens: z.number().int().min(0),
  completionTokens: z.number().int().min(0),
  costMinor: z.number().int().min(0),
  byModel: z.array(AiUsageRowSchema),
}).strict();

export const AiFetchModelsResponseSchema = z.object({
  models: z.array(z.string().trim().min(1).max(200)).max(200),
}).strict();

export const AiActivateResponseSchema = z.object({
  activeProviderId: z.string().uuid(),
}).strict();

export const AiRemoveResponseSchema = z.object({
  removed: z.literal(true),
}).strict();

export const BillingErrorResponseSchema = z.object({
  code: z.string().min(1),
}).strict();

export type QuotaResponse = z.infer<typeof QuotaResponseSchema>;
export type RedeemResponse = z.infer<typeof RedeemResponseSchema>;
export type AdminPackage = z.infer<typeof AdminPackageSchema>;
export type AdminPackageInput = z.infer<typeof AdminPackageInputSchema>;
export type AdminSummary = z.infer<typeof AdminSummarySchema>;
export type AdminRedemptionBatch = z.infer<typeof AdminRedemptionBatchSchema>;
export type AdminRedemptionRequest = z.infer<typeof AdminRedemptionRequestSchema>;
export type AdminDisableRedemptionResponse = z.infer<typeof AdminDisableRedemptionResponseSchema>;
export type AdminReissueRedemptionResponse = z.infer<typeof AdminReissueRedemptionResponseSchema>;
export type AiProvider = z.infer<typeof AiProviderSchema>;
export type AiProviderInput = z.infer<typeof AiProviderInputSchema>;
export type AiUsageSummary = z.infer<typeof AiUsageSummarySchema>;
